// assets/jersey-customization.js
import { Component } from '@theme/component';

/**
 * @typedef {object} JerseyCustomizationRefs
 * @property {HTMLInputElement} toggleCheckbox - The toggle checkbox input.
 * @property {HTMLDivElement} panel - The collapsible fields panel.
 * @property {HTMLInputElement} nameInput - The name text input.
 * @property {HTMLInputElement} numberInput - The number text input.
 * @property {HTMLSpanElement} nameCount - The character counter display.
 */

/**
 * Custom element that manages jersey name & number customization inputs.
 * - Toggle shows/hides the fields panel with a smooth CSS grid animation.
 * - When enabled: inputs are enabled and marked required.
 * - When disabled: inputs are cleared, disabled, and removed from form submission.
 * - Validates that number is a numeric value between 0 and 999.
 * @extends Component<JerseyCustomizationRefs>
 */
class JerseyCustomizationComponent extends Component {
  /** @type {ReturnType<typeof setTimeout> | null} */
  #numberErrorTimeout = null;

  handleToggle() {
    const { toggleCheckbox, panel, nameInput, numberInput } = this.refs;
    const isEnabled = toggleCheckbox.checked;

    // Animate panel open/close via data attribute (driven by CSS `data-open`)
    panel.setAttribute('data-open', String(isEnabled));
    panel.setAttribute('aria-hidden', String(!isEnabled));

    if (isEnabled) {
      this.#enableInputs(nameInput, numberInput);
      // Focus name input after animation starts
      setTimeout(() => nameInput.focus(), 50);
    } else {
      this.#disableInputs(nameInput, numberInput);
    }
  }

  handleNameInput() {
    this.#updateNameCount();
  }

  handleNumberInput() {
    const { numberInput } = this.refs;

    // Strip anything that isn't a digit
    const digitsOnly = numberInput.value.replace(/[^0-9]/g, '');

    // Clamp to max 3 chars and max value 999
    const clamped = digitsOnly.slice(0, 3);
    const numeric = parseInt(clamped, 10);

    if (clamped !== '' && numeric > 999) {
      numberInput.value = '999';
    } else {
      numberInput.value = clamped;
    }
  }

  // ── Private helpers ──────────────────────────────────────────────────────

  /**
   * @param {HTMLInputElement} nameInput
   * @param {HTMLInputElement} numberInput
   */
  #enableInputs(nameInput, numberInput) {
    for (const input of [nameInput, numberInput]) {
      input.disabled = false;
      input.required = true;
    }
    this.#updateNameCount();
  }

  /**
   * @param {HTMLInputElement} nameInput
   * @param {HTMLInputElement} numberInput
   */
  #disableInputs(nameInput, numberInput) {
    for (const input of [nameInput, numberInput]) {
      input.value = '';
      input.disabled = true;
      input.required = false;
      input.setCustomValidity('');
    }
    this.#updateNameCount();
  }

  #updateNameCount() {
    const { nameInput, nameCount } = this.refs;
    const max = nameInput.maxLength;
    const current = nameInput.value.length;
    nameCount.textContent = `${current}/${max}`;
  }
}

if (!customElements.get('jersey-customization-component')) {
  customElements.define('jersey-customization-component', JerseyCustomizationComponent);
}
